"use client";

/**
 * NotificationsDisplay - Notification list with read/unread states
 *
 * Displays notifications with:
 * - Read/unread styling
 * - Timestamps and relative dates
 * - Action links
 * - Type-based icons
 */

import React, { useState } from "react";
import { Tag, Tooltip } from "@/ui";
import { colors, spacing, radius, textStyles, primitives } from "@/ui/tokens";
import {
  Notifications,
  NotificationsActive,
  Email,
  Person,
  Work,
  Event,
  CheckCircle,
  Info,
  Warning,
  Error as ErrorIcon,
  MarkEmailRead,
  Visibility,
  Unsubscribe,
  ErrorOutline,
  MailOutline,
  ThumbUp,
  ThumbDown,
  OpenInNew,
  EventAvailable,
  RateReview,
  QuestionAnswer,
} from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
  ToolDivider,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";
import { showAllButtonClass } from "../utils";

interface Notification {
  id: string | number;
  type?: string;
  title?: string;
  message?: string;
  read?: boolean;
  createdAt?: string;
  link?: string;
  actionLabel?: string;
  metadata?: Record<string, unknown>;
  // Laravel notification fields (raw API format)
  read_at?: string | null;
  created_at?: string;
  data?: Record<string, unknown>;
}

interface NotificationsOutput {
  success?: boolean;
  notifications?: Notification[];
  unreadCount?: number;
  total?: number;
  message?: string;
  error?: string;
}

const TYPE_CONFIG: Record<
  string,
  { icon: React.ReactNode; label: string; color: "primary" | "success" | "warning" | "danger" | "info" | "neutral" }
> = {
  // Generic types
  info: { icon: <Info />, label: "Info", color: "info" },
  success: { icon: <CheckCircle />, label: "Success", color: "success" },
  warning: { icon: <Warning />, label: "Warning", color: "warning" },
  error: { icon: <ErrorIcon />, label: "Error", color: "danger" },

  // Email events
  email: { icon: <Email />, label: "Email", color: "primary" },
  email_bounced: { icon: <ErrorOutline />, label: "Email Bounced", color: "danger" },
  email_opened: { icon: <MailOutline />, label: "Email Opened", color: "success" },
  email_clicked: { icon: <OpenInNew />, label: "Email Clicked", color: "success" },
  email_unsubscribed: { icon: <Unsubscribe />, label: "Unsubscribed", color: "warning" },

  // Candidate events
  candidate: { icon: <Person />, label: "Candidate", color: "primary" },
  candidate_view_more: { icon: <Visibility />, label: "Candidate Viewed", color: "info" },
  candidate_is_interested: { icon: <ThumbUp />, label: "Candidate Interested", color: "success" },
  candidate_ask_question: { icon: <QuestionAnswer />, label: "Question Asked", color: "info" },
  candidate_not_interested: { icon: <ThumbDown />, label: "Not Interested", color: "warning" },
  candidate_book_interview: { icon: <EventAvailable />, label: "Interview Booked", color: "success" },
  candidate_give_feedback: { icon: <RateReview />, label: "Feedback Left", color: "info" },

  // Float/view events
  viewed: { icon: <Visibility />, label: "Float Viewed", color: "info" },
  float: { icon: <Work />, label: "Float", color: "primary" },
  event: { icon: <Event />, label: "Event", color: "primary" },

  // Default
  default: { icon: <Notifications />, label: "Notification", color: "neutral" },
};

/**
 * Normalize a notification from raw Laravel API format to our display format.
 * Handles both pre-normalized and raw API shapes.
 */
function normalizeNotification(n: Notification): Notification {
  // Extract data payload (Laravel notifications store details in `data`)
  const data = n.data || {};
  const title = n.title || (data.subject as string) || (data.title as string) || undefined;
  const message = n.message || (data.message as string) || (data.body as string) || undefined;
  const createdAt = n.createdAt || n.created_at || undefined;

  // Determine read status: explicit `read` bool, or infer from `read_at`
  // Laravel uses read_at: null for unread, read_at: "2024-..." for read
  let isRead: boolean;
  if (typeof n.read === "boolean") {
    isRead = n.read;
  } else {
    // Falsy read_at (null, undefined, missing) = unread
    isRead = !!n.read_at;
  }

  return {
    ...n,
    title,
    message,
    createdAt,
    read: isRead,
  };
}

function getNotificationTitle(notification: Notification): string {
  if (notification.title && notification.title !== notification.type) {
    return notification.title;
  }
  const config = TYPE_CONFIG[notification.type || "default"];
  if (config) return config.label;
  // Fallback: convert snake_case to Title Case
  return (notification.type || "Notification")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function NotificationItem({
  notification,
  onMarkRead,
}: {
  notification: Notification;
  onMarkRead?: (id: string | number) => void;
}) {
  const config = TYPE_CONFIG[notification.type || "default"] || TYPE_CONFIG.default;
  const isUnread = notification.read === false;
  const hasDetail = !!(notification.message || notification.createdAt);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: spacing[3],
        border: `1px solid ${isUnread ? colors.action.productLight : colors.border.subtle}`,
        borderRadius: radius.sm,
        marginBottom: spacing[2],
        backgroundColor: isUnread ? colors.action.productLight : colors.canvas.default,
        padding: hasDetail ? spacing[3] : `${spacing[2]}px ${spacing[3]}px`,
        transition: "all 0.2s ease",
      }}
    >
      {/* Icon */}
      <div style={{ position: "relative", flexShrink: 0 }}>
        <div
          style={{
            width: hasDetail ? 40 : 32,
            height: hasDetail ? 40 : 32,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: isUnread ? colors.action.product : colors.action.productLight,
            color: isUnread ? colors.text.inverse : colors.action.product,
          }}
        >
          {React.cloneElement(config.icon as React.ReactElement<{ style?: React.CSSProperties }>, {
            style: { fontSize: hasDetail ? 20 : 16 },
          })}
        </div>
        {isUnread && (
          <span
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: colors.action.product,
              border: `2px solid ${isUnread ? colors.action.productLight : colors.canvas.default}`,
            }}
          />
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" }}>
          <span
            style={{
              ...textStyles.body2,
              fontWeight: isUnread ? 600 : 400,
              color: isUnread ? colors.text.primary : colors.text.muted,
            }}
          >
            {getNotificationTitle(notification)}
          </span>
          {notification.createdAt && (
            <span style={{ ...textStyles.caption, color: colors.text.disabled }}>
              {formatRelativeTime(notification.createdAt)}
            </span>
          )}
        </div>

        {notification.message && (
          <p
            style={{
              ...textStyles.caption,
              color: colors.text.muted,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              margin: 0,
              marginTop: 2,
            }}
          >
            {notification.message}
          </p>
        )}
      </div>

      {/* Type tag (shown when no message detail, to give visual context) */}
      {!notification.message && (
        <Tag
          size="sm"
          color={config.color}
          variant={isUnread ? "filled" : "default"}
        >
          {config.label}
        </Tag>
      )}

      {/* Mark as read action */}
      {isUnread && onMarkRead && (
        <Tooltip content="Mark as read">
          <button
            onClick={() => onMarkRead(notification.id)}
            style={{
              border: "none",
              background: "none",
              cursor: "pointer",
              color: colors.action.product,
              padding: spacing[1],
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <MarkEmailRead style={{ fontSize: 18 }} />
          </button>
        </Tooltip>
      )}
    </div>
  );
}

const MAX_VISIBLE = 5;

export function NotificationsDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as NotificationsOutput | undefined;
  const rawNotifications = result?.notifications || [];
  const notifications = rawNotifications.map(normalizeNotification);
  const unreadCount = result?.unreadCount ?? notifications.filter((n) => n.read === false).length;
  const totalCount = result?.total ?? notifications.length;

  // Group notifications by read status
  const unreadNotifications = notifications.filter((n) => n.read === false);
  const readNotifications = notifications.filter((n) => n.read !== false);

  // Truncate: show all unread first, then fill remaining slots with read
  const needsTruncation = notifications.length > MAX_VISIBLE;
  const visibleUnread = showAll ? unreadNotifications : unreadNotifications.slice(0, MAX_VISIBLE);
  const remainingSlots = Math.max(0, MAX_VISIBLE - visibleUnread.length);
  const visibleRead = showAll ? readNotifications : readNotifications.slice(0, remainingSlots);

  const headerIcon =
    unreadCount > 0 ? (
      <div style={{ position: "relative", display: "inline-flex" }}>
        <NotificationsActive style={{ fontSize: 18 }} />
        <span
          style={{
            position: "absolute",
            top: -6,
            right: -10,
            backgroundColor: colors.signal.danger,
            color: colors.text.inverse,
            borderRadius: radius.pill,
            fontSize: 10,
            fontWeight: 700,
            minWidth: 18,
            height: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 4px",
          }}
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      </div>
    ) : (
      <Notifications style={{ fontSize: 18 }} />
    );

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={`Notifications${totalCount > 0 ? ` (${totalCount})` : ""}`}
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={headerIcon}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {/* Summary Bar */}
        {!isLoading && notifications.length > 0 && (
          <div
            style={{
              paddingLeft: spacing[5],
              paddingRight: spacing[5],
              paddingTop: spacing[3],
              paddingBottom: spacing[3],
              borderBottom: `1px solid ${colors.border.subtle}`,
              display: "flex",
              alignItems: "center",
              gap: spacing[4],
            }}
          >
            {unreadCount > 0 && (
              <Tag
                icon={<NotificationsActive style={{ fontSize: 16 }} />}
                size="sm"
                color="primary"
                variant="filled"
              >
                {unreadCount} unread
              </Tag>
            )}
            {readNotifications.length > 0 && (
              <Tag
                icon={<CheckCircle style={{ fontSize: 16 }} />}
                size="sm"
                color="neutral"
                variant="default"
              >
                {readNotifications.length} read
              </Tag>
            )}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label="Loading notifications..." />
          </div>
        )}

        {/* Error State */}
        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {/* Notifications List */}
        {!isLoading && notifications.length > 0 && (
          <div style={{ padding: spacing[4] }}>
            {/* Unread Section */}
            {visibleUnread.length > 0 && (
              <>
                <span
                  style={{
                    ...textStyles.label,
                    color: colors.action.product,
                    fontWeight: 700,
                    display: "block",
                    marginBottom: spacing[2],
                  }}
                >
                  Unread
                </span>
                <div style={{ marginBottom: spacing[4] }}>
                  {visibleUnread.map((notification) => (
                    <NotificationItem key={notification.id} notification={notification} />
                  ))}
                </div>
              </>
            )}

            {/* Read Section */}
            {visibleRead.length > 0 && (
              <>
                {visibleUnread.length > 0 && (
                  <ToolDivider spaced />
                )}
                <span
                  style={{
                    ...textStyles.label,
                    color: colors.text.muted,
                    fontWeight: 600,
                    display: "block",
                    marginBottom: spacing[2],
                  }}
                >
                  Earlier
                </span>
                <div>
                  {visibleRead.map((notification) => (
                    <NotificationItem key={notification.id} notification={notification} />
                  ))}
                </div>
              </>
            )}

            {/* Show all toggle */}
            {needsTruncation && !showAll && (
              <button onClick={() => setShowAll(true)} className={showAllButtonClass}>
                Show all {notifications.length} notifications
              </button>
            )}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && notifications.length === 0 && !isError && (
          <ToolEmptyState icon={<Notifications />} message={result?.message || "No notifications"} />
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default NotificationsDisplay;
